import { Component, OnInit, inject, signal } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { PaymentTimerComponent } from "./payment-timer/payment-timer.component";
import { ApprovalService } from "src/app/services/approval.service";
import { GetApprovalRequest, GetApprovedRequest, GetRejectedRequest } from "src/app/models/approval/approval";
import { PaginationComponent } from "../../pagination/pagination.component";
import Swal from "sweetalert2";
import { FormsModule } from "@angular/forms";
import { LoadingService } from "src/app/services/loading.service";
import { finalize } from "rxjs";
import { PaymentStatus } from "src/app/enums/payment-status";

type Tap = "approved" | "completed" | "rejected" | "expired";

@Component({
  selector: "app-approve-requests",
  imports: [CommonModule, PaymentTimerComponent, PaginationComponent, DatePipe, FormsModule],
  templateUrl: "./approve-requests.component.html",
  styleUrl: "./approve-requests.component.css",
})
export class ApproveRequestsComponent implements OnInit {
  private approvalService = inject(ApprovalService);
  private loadingSvc = inject(LoadingService);

  pendingRequests = signal<GetApprovalRequest[]>([]);
  approvedRequests = signal<GetApprovedRequest[]>([]);
  completedRequests = signal<GetApprovedRequest[]>([]);
  rejectedRequests = signal<GetRejectedRequest[]>([]);
  expiredRequests = signal<GetApprovedRequest[]>([]);

  isLoading = signal(false);
  approvedLoading = signal(false);
  completedLoading = signal(false);
  rejectedLoading = signal(false);
  expiredLoading = signal(false);

  selectedTab = signal<Tap>("approved");

  pendingPage = signal(1);
  pendingPageSize = signal(10);
  pendingTotal = signal(0);

  approvedPage = signal(1);
  approvedPageSize = signal(10);
  approvedTotal = signal(0);

  completedPage = signal(1);
  completedPageSize = signal(10);
  completedTotal = signal(0);

  rejectedPage = signal(1);
  rejectedPageSize = signal(10);
  rejectedTotal = signal(0);

  expiredPage = signal(1);
  expiredPageSize = signal(10);
  expiredTotal = signal(0);

  filterStartDate = signal<string | null>(null);
  filterEndDate = signal<string | null>(null);

  filterKey = signal<string | null>(null);
  filterValue = signal<string | null>(null);

  filterTapStartDate = signal<string | null>(null);
  filterTapEndDate = signal<string | null>(null);

  filterTapKey = signal<string | null>(null);
  filterTapValue = signal<string | null>(null);

  ngOnInit() {
    this.loadPendingRequests();
    this.loadApprovedRequests();
  }

  loadPendingRequests() {
    this.isLoading.set(true);
    this.approvalService
      .getPendingRequests({
        startDate: this.filterStartDate() || undefined,
        endDate: this.filterEndDate() || undefined,
        page: this.pendingPage(),
        pageSize: this.pendingPageSize(),
        filterKey: this.filterKey() ?? undefined,
        filterValue: this.filterValue() ?? undefined
      })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.pendingRequests.set(response.items);
          this.pendingTotal.set(response.totalItems);
        },
      });
  }

  loadApprovedRequests() {
    this.approvedLoading.set(true);
    this.approvalService
      .getApprovedRequests({
        page: this.approvedPage(),
        pageSize: this.approvedPageSize(),
        paymentStatusId: PaymentStatus.Pending,
        startDate: this.filterTapStartDate() || undefined,
        endDate: this.filterTapEndDate() || undefined,
        filterKey: this.filterTapKey() ?? undefined,
        filterValue: this.filterTapValue() ?? undefined
      })
      .pipe(finalize(() => this.approvedLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.approvedRequests.set(response.items);
          this.approvedTotal.set(response.totalItems);
        },
      });
  }

  loadCompletedRequests() {
    this.completedLoading.set(true);
    this.approvalService
      .getApprovedRequests({
        page: this.completedPage(),
        pageSize: this.completedPageSize(),
        paymentStatusId: PaymentStatus.Paid,
        startDate: this.filterTapStartDate() || undefined,
        endDate: this.filterTapEndDate() || undefined,
        filterKey: this.filterTapKey() ?? undefined,
        filterValue: this.filterTapValue() ?? undefined
      })
      .pipe(finalize(() => this.completedLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.completedRequests.set(response.items);
          this.completedTotal.set(response.totalItems);
        },
      });
  }

  loadRejectedRequests() {
    this.rejectedLoading.set(true);
    this.approvalService
      .getRejectedRequests({
        page: this.rejectedPage(),
        pageSize: this.rejectedPageSize(),
        startDate: this.filterTapStartDate() || undefined,
        endDate: this.filterTapEndDate() || undefined,
        filterKey: this.filterTapKey() ?? undefined,
        filterValue: this.filterTapValue() ?? undefined
      })
      .pipe(finalize(() => this.rejectedLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.rejectedRequests.set(response.items);
          this.rejectedTotal.set(response.totalItems);
        },
      });
  }

  loadExpiredRequests() {
    this.expiredLoading.set(true);
    this.approvalService
      .getApprovedRequests({
        page: this.expiredPage(),
        pageSize: this.expiredPageSize(),
        paymentStatusId: PaymentStatus.Expired,
        startDate: this.filterTapStartDate() || undefined,
        endDate: this.filterTapEndDate() || undefined,
        filterKey: this.filterTapKey() ?? undefined,
        filterValue: this.filterTapValue() ?? undefined
      })
      .pipe(finalize(() => this.expiredLoading.set(false)))
      .subscribe({
        next: (response) => {
          this.expiredRequests.set(response.items);
          this.expiredTotal.set(response.totalItems);
        },
      });
  }

  onPendingPageChange(page: number) {
    this.pendingPage.set(page);
    this.loadPendingRequests();
  }

  onPendingPageSizeChange(size: number) {
    this.pendingPageSize.set(size);
    this.pendingPage.set(1);
    this.loadPendingRequests();
  }

  onApprovedPageChange(page: number) {
    this.approvedPage.set(page);
    this.loadApprovedRequests();
  }

  onApprovedPageSizeChange(size: number) {
    this.approvedPageSize.set(size);
    this.approvedPage.set(1);
    this.loadApprovedRequests();
  }

  onCompletedPageChange(page: number) {
    this.completedPage.set(page);
    this.loadCompletedRequests();
  }

  onCompletedPageSizeChange(size: number) {
    this.completedPageSize.set(size);
    this.completedPage.set(1);
    this.loadCompletedRequests();
  }

  onRejectedPageChange(page: number) {
    this.rejectedPage.set(page);
    this.loadRejectedRequests();
  }

  onRejectedPageSizeChange(size: number) {
    this.rejectedPageSize.set(size);
    this.rejectedPage.set(1);
    this.loadRejectedRequests();
  }

  onExpiredPageChange(page: number) {
    this.expiredPage.set(page);
    this.loadExpiredRequests();
  }

  onExpiredPageSizeChange(size: number) {
    this.expiredPageSize.set(size);
    this.expiredPage.set(1);
    this.loadExpiredRequests();
  }

  approveRequest(request: GetApprovalRequest) {
    Swal.fire({
      title: "¿Aprobar solicitud?",
      text: "Confirma que deseas aprobar esta solicitud.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Aprobar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#0d1174 ",
      cancelButtonColor: "#6c757d",
      preConfirm: () => {
        this.loadingSvc.show();
        this.approvalService
          .approveRequest({ requestId: request.requestId })
          .pipe(finalize(() => this.loadingSvc.hide()))
          .subscribe({
            next: () => {
              this.loadPendingRequests();
              this.loadApprovedRequests();
              Swal.fire("Solicitud aprobada", "La solicitud ha sido aprobada correctamente.", "success");
            },
            error: (error) => {
              Swal.fire("Error", error[0] || "Ocurrió un error al aprobar la solicitud.", "error");
            },
          });
      },
    });
  }

  rejectRequest(request: GetApprovalRequest) {
    Swal.fire({
      title: "¿Rechazar solicitud?",
      text: "Puedes indicar una razón del rechazo (opcional):",
      input: "text",
      inputPlaceholder: "Escriba aquí la razón (opcional)...",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Rechazar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      preConfirm: (reason) => {
        this.loadingSvc.show();
        this.approvalService
          .rejectRequest({
            requestId: request.requestId,
            rejectionReason: reason.trim(),
          })
          .pipe(finalize(() => this.loadingSvc.hide()))
          .subscribe({
            next: () => {
              this.loadPendingRequests();
              this.loadRejectedRequests();
              Swal.fire("Solicitud rechazada", "La solicitud ha sido rechazada correctamente.", "success");
            },
            error: (error) => {
              Swal.fire("Error", error[0] || "Ocurrió un error al rechazar la solicitud.", "error");
            },
          });
      },
    });
  }

  selectTab(tab: Tap) {
    this.selectedTab.set(tab);
    this.filterTapStartDate.set(null);
    this.filterTapEndDate.set(null);
    this.filterTapKey.set(null);
    this.filterTapValue.set(null);
    switch (this.selectedTab()) {
      case "approved":
        this.approvedPage.set(1);
        this.loadApprovedRequests();
        break;
      case "completed":
        this.completedPage.set(1);
        this.loadCompletedRequests();
        break;
      case "rejected":
        this.rejectedPage.set(1);
        this.loadRejectedRequests();
        break;
      case "expired":
        this.expiredPage.set(1);
        this.loadExpiredRequests();
        break;
    }
  }

  applyFilters() {
    this.pendingPage.set(1);
    this.loadPendingRequests();
  }

  clearFilters() {
    this.filterStartDate.set(null);
    this.filterEndDate.set(null);
    this.filterKey.set(null);
    this.filterValue.set(null);
    this.applyFilters();
  }

  applyTapFilters() {
    switch (this.selectedTab()) {
      case "approved":
        this.approvedPage.set(1);
        this.loadApprovedRequests();
        break;
      case "completed":
        this.completedPage.set(1);
        this.loadCompletedRequests();
        break;
      case "rejected":
        this.rejectedPage.set(1);
        this.loadRejectedRequests();
        break;
      case "expired":
        this.expiredPage.set(1);
        this.loadExpiredRequests();
        break;
    }
  }

  clearTapFilters() {
    this.filterTapStartDate.set(null);
    this.filterTapEndDate.set(null);
    this.filterTapKey.set(null);
    this.filterTapValue.set(null);
    this.applyTapFilters();
  }
}